import React, { useRef, useState, useEffect, useCallback } from 'react';

/**
 * Zoom + Pan Image Viewer (WhatsApp-style).
 *
 * • Trackpad / mouse-wheel pinch → smooth zoom around cursor
 * • Two-finger pinch on touch → smooth zoom around midpoint
 * • Ctrl / Cmd + wheel → zoom (matches every OS convention)
 * • Once zoomed in (scale > 1) the image becomes draggable — both mouse-drag
 *   and single-finger drag pan it in every direction, clamped so the image
 *   can't leak past a comfortable margin.
 * • Double-click / double-tap toggles between 1× and 2×.
 * • Reset button + keyboard `0` snaps back to fit.
 */
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const ZoomableImage = ({ src, alt = 'Preview', className = '' }) => {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const pinchRef = useRef(null);

  // Reset transforms whenever the src changes so navigating between photos
  // in a lightbox doesn't leave the next one panned/zoomed unexpectedly.
  useEffect(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, [src]);

  // Keyboard shortcut: press "0" to reset.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '0') { setScale(1); setTranslate({ x: 0, y: 0 }); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Clamp translation so image doesn't slide entirely off-screen when panning
  const clampTranslate = useCallback((tx, ty, s) => {
    const el = containerRef.current;
    if (!el) return { x: tx, y: ty };
    const rect = el.getBoundingClientRect();
    const maxX = (rect.width * (s - 1)) / 2 + 60;
    const maxY = (rect.height * (s - 1)) / 2 + 60;
    return { x: clamp(tx, -maxX, maxX), y: clamp(ty, -maxY, maxY) };
  }, []);

  // Wheel / trackpad pinch (Ctrl+wheel is macOS pinch convention)
  const onWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;  // let normal scroll pass through
    e.preventDefault();
    const delta = -e.deltaY * 0.01;
    setScale((s) => {
      const next = clamp(s + delta, 1, 5);
      if (next === 1) setTranslate({ x: 0, y: 0 });
      return next;
    });
  };

  // Two-finger touch pinch
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      pinchRef.current = {
        startDist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        startScale: scale,
      };
      dragRef.current = null;
    } else if (e.touches.length === 1 && scale > 1) {
      dragRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        startTx: translate.x,
        startTy: translate.y,
      };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const [a, b] = e.touches;
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const next = clamp(pinchRef.current.startScale * (d / pinchRef.current.startDist), 1, 5);
      setScale(next);
      if (next === 1) setTranslate({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && dragRef.current && scale > 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - dragRef.current.startX;
      const dy = e.touches[0].clientY - dragRef.current.startY;
      setTranslate(clampTranslate(dragRef.current.startTx + dx, dragRef.current.startTy + dy, scale));
    }
  };
  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinchRef.current = null;
    if (e.touches.length === 0) dragRef.current = null;
  };

  // Mouse drag when zoomed in
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, startTx: translate.x, startTy: translate.y };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      setTranslate(clampTranslate(dragRef.current.startTx + dx, dragRef.current.startTy + dy, scale));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Double-click / double-tap toggles zoom
  const onDoubleClick = () => {
    if (scale > 1) {
      setScale(1);
      setTranslate({ x: 0, y: 0 });
    } else {
      setScale(2);
    }
  };

  return (
    <div
      ref={containerRef}
      onWheel={onWheel}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      className={`relative w-full h-full flex items-center justify-center overflow-hidden touch-none select-none ${className}`}
      data-testid="zoomable-image"
      style={{ cursor: scale > 1 ? (dragRef.current ? 'grabbing' : 'grab') : 'zoom-in' }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="max-w-full max-h-full object-contain pointer-events-none"
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transformOrigin: 'center center',
          transition: dragRef.current || pinchRef.current ? 'none' : 'transform 120ms ease-out',
        }}
      />
      {scale !== 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); setScale(1); setTranslate({ x: 0, y: 0 }); }}
          className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white text-[11px] font-bold px-2.5 py-1 rounded-full backdrop-blur"
          data-testid="zoomable-reset"
          title="Reset zoom (press 0)"
        >
          {scale.toFixed(1)}× · Reset
        </button>
      )}
    </div>
  );
};

export default ZoomableImage;
